import Header from '@/components/atoms/Header';
import PageContent from '@/components/atoms/Layouts/PageContent';
import Trip from '@/components/atoms/Trip';
import { canonicalUrl } from '@/hooks/index';
import { ExtendedTrip } from '@/types/extended';
import { capitalize } from '@/utils/index';
import { Box, VStack } from '@chakra-ui/react';
import { PrismaClient } from '@prisma/client';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import safeJsonStringify from 'safe-json-stringify';

interface Props {
	trips: ExtendedTrip[];
	coverPhoto: string;
}

export default function TripsList({ trips, coverPhoto }: Props) {
	const router = useRouter();
	const { location } = router.query;

	const formattedLocation = capitalize((location as string).replace(/-/g, ' '));

	const backlinks = [{ label: 'Explore', link: '/' }];

	return (
		<>
			<NextSeo
				title={`Trips to ${formattedLocation}`}
				description={`Browse trips curated by others to ${formattedLocation}`}
				canonical={`${canonicalUrl}/destinations/${location}`}
				openGraph={{
					type: 'website',
					url: `${canonicalUrl}/destinations/${location}`,
					title: `Trips to ${formattedLocation}`,
					description: `Browse trips curated by others to ${formattedLocation}`,
					images: [
						{
							url: coverPhoto,
							alt: `Trips to ${formattedLocation}`,
						},
					],
					site_name: 'WheredYouGo',
				}}
			/>
			<Header title={`Trips to ${formattedLocation}`} backlinks={backlinks} />
			<PageContent>
				<Box pb='16px' px={{ base: '16px', md: '64px' }}>
					<VStack spacing={3}>
						{trips?.map((trip) => (
							<Trip key={trip.id} data={trip as ExtendedTrip} />
						))}
					</VStack>
				</Box>
			</PageContent>
		</>
	);
}

export const getStaticPaths: GetStaticPaths = async () => {
	const prisma = new PrismaClient();
	const destinations = await prisma.destination.findMany({
		orderBy: { updatedAt: 'desc' },
		include: { trips: { orderBy: { updatedAt: 'desc' } } },
	});

	const destinationsWithTrips = destinations
		.filter((destination) => Boolean(destination.trips.filter((trip) => !trip.draft).length))
		.map((el) => ({ ...el, trips: el.trips.filter((trip) => !trip.draft) }));

	const top5 = destinationsWithTrips.sort((a, b) => b.trips.length - a.trips.length).slice(0, 5);

	const paths = top5.map((el) => ({
		params: { location: el.location.toLowerCase().replace(/ /g, '-') },
	}));

	return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
	const prisma = new PrismaClient();
	const trips = await prisma.trip.findMany({
		where: { AND: [{ location: (params?.location as string).replace(/-/g, ' ') }, { draft: false }] },
		include: { author: true, ttd: true, photos: true, housing: { include: { photos: true } } },
		orderBy: { updatedAt: 'desc' },
	});

	if (!trips.length) return { notFound: true };

	const { coverPhoto } = (await prisma.destination.findUnique({
		where: { location: (params?.location as string).replace(/-/g, ' ') },
		select: { coverPhoto: true },
	})) as { coverPhoto: string };

	return { props: { trips: JSON.parse(safeJsonStringify(trips)), coverPhoto } };
};
